import { faBuilding, faGasPump, faMapLocation, faUser } from '@fortawesome/free-solid-svg-icons'
import { FornecedorItens } from '../../../../services/Fornecedores'
import dayjs, { Dayjs } from 'dayjs'
import { Abastecimento } from '../../../../services/abastecimento/GET'
import {
  desmascararNumero,
  dinheiro,
  horaComSegundos,
  mascaraValor,
  mascararValorInicial,
  maskCNPJ,
  removerDecimal,
} from '../../../../utils'

/**Define se o abastecimento foi feito em um posto ou em uma propriedade */
export const tabs_local = [
  { descricao: 'Posto', icone: faGasPump },
  { descricao: 'Propriedade', icone: faMapLocation },
]

/**Define quem assume o custo do abastecimento */
export const tabs_responsavel_custo = [
  { descricao: 'Empresa', icone: faBuilding },
  { descricao: 'Pessoal', icone: faUser },
]

/**Postos são os fornecedores marcados como `Posto de combustível?` no cadastro,
 * marcação que é gravada como a tag `Abastecimento`
*/
export function filtrarPostos(fornecedores: FornecedorItens[]): FornecedorItens[] {
  return (fornecedores ?? []).filter((item) => (item?.tags ?? []).includes("Abastecimento"))
}

/**Junta os campos de endereço do fornecedor em uma única linha
 *
 * exemplo: `Av. Brasil, 1500 - Centro - Sorriso/MT`
*/
export function montarEndereco(fornecedor: FornecedorItens): string {
  const cidade = fornecedor?.cidade?.[0]

  const partes = [
    [fornecedor?.endereco, fornecedor?.endereco_numero].filter(Boolean).join(', '),
    fornecedor?.bairro,
    cidade?.uf_sigla ? `${cidade?.nome}/${cidade?.uf_sigla}` : cidade?.nome,
  ]

  return partes.filter(Boolean).join(' - ')
}

/**Valores do formulário que compõem o payload enviado para a API */
export type ValoresAbastecimento = {
  veiculo_selecionado_id: number
  usuario_selecionado_id: number
  tab_local: string
  posto_selecionado_id: number
  nome_posto: string
  cnpj: string
  endereco: string
  propriedade_selecionada_id: number
  odom_atual: string
  qntd_litros: string
  valor_total: string
  tipo_combustivel_id: number
  data: Dayjs | null
  hora: string
  tanque_cheio: boolean
  pagamento_efetuado: boolean
  responsavel_custo: string
  necessario_reembolso: boolean
  desconto_folha: boolean
  forma_pagamento_id: number
  arquivo: File | null
}

/**Monta o corpo da requisição de cadastro/atualização a partir dos valores do formulário
 *
 * usada tanto para os valores atuais quanto para os valores originais do registro em edição,
 * garantindo que a comparação entre eles seja feita já no formato que a API recebe
*/
export function montarPayloadAbastecimento(valores: ValoresAbastecimento) {
  const em_posto = valores?.tab_local === 'Posto'

  return {
    cod_veiculo: valores?.veiculo_selecionado_id,
    cod_usuario: valores?.usuario_selecionado_id,
    tipo_local: em_posto ? 'posto' : 'propriedade',
    ...(em_posto
      ? {
        // o posto só vai quando foi selecionado da lista, digitado manualmente não tem código
        ...(valores?.posto_selecionado_id ? { cod_posto: valores?.posto_selecionado_id } : {}),
        posto: {
          nome_posto: valores?.nome_posto,
          cnpj: valores?.cnpj,
          endereco: valores?.endereco,
        },
      }
      : { cod_propriedade: valores?.propriedade_selecionada_id }),
    abastecimento: {
      odom_atual: desmascararNumero(valores?.odom_atual),
      qntd_litros: desmascararNumero(valores?.qntd_litros),
      ...(em_posto ? { valor_total: desmascararNumero(mascaraValor(valores?.valor_total)) } : {}),
      tipo_combustivel: valores?.tipo_combustivel_id,
      data: valores?.data?.format('YYYY-MM-DD'),
      hora: horaComSegundos(valores?.hora),
      tanque_cheio: valores?.tanque_cheio,
      pago: valores?.pagamento_efetuado,
      tipo_uso: valores?.responsavel_custo === 'Empresa' ? 1 : 2,
      necessario_reembolso: valores?.necessario_reembolso,
      desconto_folha: valores?.desconto_folha,
      forma_pagamento: valores?.forma_pagamento_id,
    },
    arquivo: valores?.arquivo,
  }
}

/**Traduz o registro vindo da API para os valores do formulário
 *
 * usada ao abrir a gaveta de edição, tanto para preencher os campos quanto para guardar
 * o estado original do registro, que depois é comparado com o que o usuário deixou nos campos
*/
export function valoresDoAbastecimento(item: Abastecimento): ValoresAbastecimento {
  return {
    veiculo_selecionado_id: item?.cod_veiculo,
    usuario_selecionado_id: item?.cod_usuario,
    tab_local: item?.cod_propriedade ? 'Propriedade' : 'Posto',
    posto_selecionado_id: item?.cod_posto,
    nome_posto: item?.nome_posto,
    cnpj: maskCNPJ(item?.cnpj),
    endereco: item?.endereco,
    propriedade_selecionada_id: item?.cod_propriedade ?? null,
    odom_atual: removerDecimal(item?.odom_atual),
    qntd_litros: mascararValorInicial(item?.qntd_litros),
    valor_total: mascararValorInicial(item?.valor_total),
    tipo_combustivel_id: item?.tipo_combustivel,
    data: item?.data ? dayjs(item?.data) : null,
    hora: item?.hora,
    tanque_cheio: item?.tanque_cheio,
    pagamento_efetuado: item?.pagamento_efetuado,
    responsavel_custo: item?.tipo_uso_nome ?? (item?.responsavel_custo === 'pessoal' ? 'Pessoal' : 'Empresa'),
    necessario_reembolso: item?.necessario_reembolso,
    desconto_folha: item?.desconto_folha,
    forma_pagamento_id: item?.forma_pagamento,
    // o arquivo já gravado não é reenviado, só vai quando o usuário escolhe um novo cupom
    arquivo: null,
  }
}

/**`null`, `undefined` e `''` significam a mesma coisa aqui: campo não preenchido */
function naoPreenchido(valor: unknown): boolean {
  return valor === null || valor === undefined || valor === ''
}

/**A API devolve números como texto (`"125000.00"`), então a comparação é feita pelo texto do valor
 *
 * dois campos não preenchidos são considerados iguais, e um campo que não estava preenchido antes
 * e agora tem valor conta como alterado
*/
function mesmoValor(atual: unknown, original: unknown): boolean {
  if (naoPreenchido(atual) && naoPreenchido(original)) return true

  return String(atual) === String(original)
}

/**Os grupos aninhados do payload (`posto` e `abastecimento`) são comparados campo a campo */
function ehGrupoDeCampos(valor: unknown): boolean {
  return typeof valor === 'object' && valor !== null
}

/**Mantém apenas os campos que o usuário alterou ou que não estavam preenchidos antes
 *
 * `tipo_local` sempre é enviado, pois é o campo que diz para a API como interpretar os demais
 * (dados do posto x código da propriedade)
*/
export function apenasCamposAlterados(atual: object, original: object): any {
  const alterados: any = {}

  for (const chave of Object.keys(atual ?? {})) {
    const valor_atual = atual?.[chave]
    const valor_original = original?.[chave]

    if (chave === 'tipo_local') {
      alterados[chave] = valor_atual
      continue
    }

    // um arquivo novo é sempre um dado alterado, e a ausência dele mantém o cupom já gravado
    if (valor_atual instanceof File) {
      alterados[chave] = valor_atual
      continue
    }

    if (ehGrupoDeCampos(valor_atual)) {
      const filho = apenasCamposAlterados(valor_atual, valor_original ?? {})

      if (Object.keys(filho).length > 0) alterados[chave] = filho
      continue
    }

    if (!mesmoValor(valor_atual, valor_original)) alterados[chave] = valor_atual
  }

  return alterados
}

/**A API devolve o motivo da inconsistência em `inconsistencia`; sem motivo o registro está certo */
export function temInconsistencia(inconsistencia?: string): boolean {
  return String(inconsistencia ?? '').trim() !== ''
}

export function valorDoAbastecimento(valor_total: string, tipo_local?: 'posto' | 'propriedade'): string {
  if (tipo_local === 'propriedade') return '-'

  const valor = Number(valor_total)

  // no posto o valor é preenchido no cadastro, faltando ele é dado ausente e precisa ficar visível
  if (String(valor_total ?? '').trim() === '' || !isFinite(valor)) return '??'

  return dinheiro(valor)
}